// TASK-73 Phase 1 PR-B — generateMatchesForRound + supporting pure helpers.
//
// Per Rickard's TASK-73_BESLUT_OCH_PLAN.md:
//   - Match generation is per-omgång (each league_rounds row holds its
//     own complete round-robin per division)
//   - Generera = create pairings + auto-place in same transaction
//
// This module:
//   1. `buildRoundMatchInserts(args)` — pure helper. Given divisions +
//      teams + ball-holder principle, emits the league_matches rows
//      ready for INSERT. Unit-testable.
//   2. `buildAutoScheduleInput(args)` — pure helper. Re-keys the helper
//      module's inputs into the autoSchedule contract shape (date / time
//      strings — autoSchedule still uses non-canonical keys internally;
//      writes use scheduled_date / scheduled_time per Phase 0 Fix 2).
//   3. `generateMatchesForRound(supabase, args)` — I/O orchestrator.
//      Fetches round + league + divisions + teams + courts + existing
//      bookings + existing placed matches, calls the pure helpers,
//      bulk-inserts new matches, runs autoSchedule, bulk-updates
//      placements with canonical scheduled_date / scheduled_time.
//
// Reuses:
//   - `generateRoundRobin` from src/lib/bergerSchedule.ts (Berger circle)
//   - `autoSchedule` from src/lib/autoSchedule.ts (slot allocator)
//   - `pickBallHolder` from src/lib/ballHolder.ts (per-match ball-holder)
//
// PR-B intentionally still writes the legacy `round int` column (Berger
// week 1..N-1) alongside the new `round_id` FK. PR-X drops `round` after
// production proof.

import { addDays, format, parseISO } from "date-fns";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database, Tables, TablesInsert } from "@/integrations/supabase/types";
import { generateRoundRobin } from "@/lib/bergerSchedule";
import { autoSchedule, type ScheduleInput } from "@/lib/autoSchedule";
import { pickBallHolder, type BallHolderPrinciple, type BallHolderRef, type BallHolderTeam } from "@/lib/ballHolder";
import { fromStockholmWallClock, addMinutes } from "@/lib/timezone";

// ─── PR-1b: shared mapper helpers ─────────────────────────────────────────
//
// Extracted from the two duplicate mapper branches in
// `generateMatchesForRound` (skipIfMatchesExist true + false paths). Picks
// the first 2 primaries by joined_at, preferring user_id when present and
// falling back to customer_id — so customer-only team members are now
// represented to `pickBallHolder` instead of being silently dropped (per
// the O1 finding + customer-player-architecture audit).
//
// Returns BallHolderRef-shaped slots so the picker can discriminate
// downstream (PR-1b adapter still NULLs the customer-kind write; PR-1c
// flips that fence).

type TeamMemberMinimal = {
  user_id: string | null;
  customer_id: string | null;
  role: string;
  joined_at: string;
};

function toBallHolderRef(m: TeamMemberMinimal | undefined): BallHolderRef | null {
  if (!m) return null;
  if (m.user_id) return { kind: "user", id: m.user_id };
  if (m.customer_id) return { kind: "customer", id: m.customer_id };
  return null;
}

function extractPrimariesForBallHolder(
  members: TeamMemberMinimal[] | null | undefined,
): { primary1: BallHolderRef | null; primary2: BallHolderRef | null } {
  const primaries = (members ?? [])
    .filter((m) => m.role === "primary" && (m.user_id || m.customer_id))
    .sort((a, b) => a.joined_at.localeCompare(b.joined_at));
  return {
    primary1: toBallHolderRef(primaries[0]),
    primary2: toBallHolderRef(primaries[1]),
  };
}

// ─── Public types ────────────────────────────────────────────────────────

export type GenerateMatchesForRoundResult = {
  roundId: string;
  divisionsProcessed: number;
  matchesCreated: number;
  matchesPlaced: number;
  matchesUnplaced: number;
  /**
   * Per-match diagnostic info for matches that were created but couldn't be
   * placed. `reason` is either the sentinel `"no_available_slot"` (the most
   * common case — autoSchedule ran but found no free slot for this match) or
   * the message of an exception thrown by autoSchedule. The optional name
   * fields are populated since Path D so the UI can render
   * `"<home> vs <away> (<division>): <reason>"` without an extra round-trip.
   * Older tests / consumers that don't read the name fields keep working —
   * the new fields are strictly additive.
   */
  unplacedReasons: Array<{
    matchId: string;
    reason: string;
    homeTeamName?: string | null;
    awayTeamName?: string | null;
    divisionName?: string | null;
  }>;
  /** Set when a soft skip occurred (e.g. division already had matches with skipIfMatchesExist=true). */
  skipped?: { reason: "matches_exist_for_round"; divisionsSkipped: number };
};

// Pure-helper inputs (no Supabase dependency). Used by both the
// orchestrator and the unit tests.

export type DivisionForGeneration = {
  id: string;
  /** From league_rounds.start_date — used to derive Berger week dates. */
  teams: Array<{
    id: string;
    /**
     * First 2 primaries by joined_at (NULL if missing).
     * PR-1b: each slot is now a discriminated `BallHolderRef`
     * (`kind: 'user' | 'customer'`) instead of a raw user-id string.
     */
    primary1: BallHolderRef | null;
    primary2: BallHolderRef | null;
  }>;
  /** play_schedule jsonb on the divisions row — may be null. */
  playSchedule: Record<string, { start: string; end: string; court_ids?: string[] }[]> | null;
};

export type BuildInsertsArgs = {
  roundId: string;
  leagueId: string;
  divisions: DivisionForGeneration[];
  ballHolderPrinciple: BallHolderPrinciple;
};

export type LeagueMatchInsert = TablesInsert<"league_matches">;

// ─── Pure helper 1: build inserts ────────────────────────────────────────
//
// Given the per-division team + ball-holder context, emit one insert row
// per pairing. Round_id is set; legacy `round int` is set to the Berger
// week (1..N-1). status='scheduled'. ball_holder_user_id resolved per
// match via pickBallHolder.

export function buildRoundMatchInserts(args: BuildInsertsArgs): {
  inserts: LeagueMatchInsert[];
  divisionsProcessed: number;
} {
  const { roundId, leagueId, divisions, ballHolderPrinciple } = args;
  const inserts: LeagueMatchInsert[] = [];
  let divisionsProcessed = 0;

  for (const div of divisions) {
    if (div.teams.length < 2) continue;
    divisionsProcessed++;
    const teamIds = div.teams.map((t) => t.id);
    const primaryByTeam = new Map(
      div.teams.map((t) => [t.id, { p1: t.primary1, p2: t.primary2 }]),
    );

    const pairings = generateRoundRobin(teamIds);

    // Index per Berger round so pickBallHolder can rotate evenly.
    const matchesByRound = new Map<number, typeof pairings>();
    for (const p of pairings) {
      if (!matchesByRound.has(p.round)) matchesByRound.set(p.round, []);
      matchesByRound.get(p.round)!.push(p);
    }

    for (const m of pairings) {
      const roundMatches = matchesByRound.get(m.round)!;
      const matchIdx = roundMatches.indexOf(m);
      const homePair = primaryByTeam.get(m.home) ?? { p1: null, p2: null };
      const awayPair = primaryByTeam.get(m.away) ?? { p1: null, p2: null };
      const homeBH: BallHolderTeam = { id: m.home, player1: homePair.p1, player2: homePair.p2 };
      const awayBH: BallHolderTeam = { id: m.away, player1: awayPair.p1, player2: awayPair.p2 };
      const ballHolder = pickBallHolder(
        {
          id: "", // not read by pickBallHolder
          round: m.round,
          match_index_in_round: matchIdx,
          home_team: homeBH,
          away_team: awayBH,
        },
        ballHolderPrinciple,
      );
      inserts.push({
        league_id: leagueId,
        division_id: div.id,
        home_team_id: m.home,
        away_team_id: m.away,
        round: m.round, // Berger week 1..N-1 (legacy column kept until PR-X)
        round_id: roundId, // canonical FK from PR-A
        status: "scheduled",
        // Dual-FK write per BallHolderRef.kind discriminator.
        // DB-level mutex CHECK (league_matches_ball_holder_mutex, PR-1a) enforces
        // at most one column non-null per row — kind-discriminator in code makes
        // this trivially true: exactly one of the two ternaries is `id`, the other null.
        ball_holder_user_id: ballHolder?.kind === "user" ? ballHolder.id : null,
        ball_holder_customer_id: ballHolder?.kind === "customer" ? ballHolder.id : null,
      } as LeagueMatchInsert);
    }
  }

  return { inserts, divisionsProcessed };
}

// ─── Pure helper 2: build autoSchedule input ─────────────────────────────
//
// Re-keys the helper module's domain types into autoSchedule's input
// contract (date/time strings, not scheduled_date/scheduled_time).

export type AutoScheduleArgs = {
  /** Inserted matches to place. matchId is league_matches.id (assigned by DB after INSERT). */
  matchesToPlace: Array<{ id: string; round: number; home_team_id: string; away_team_id: string; division_id: string | null }>;
  /** league.play_schedule fallback (used when division has no schedule). */
  leaguePlaySchedule: Record<string, { start: string; end: string }[]> | null;
  /** Divisions with their per-division play_schedule (overrides league fallback). */
  divisions: DivisionForGeneration[];
  courts: Array<{ id: string }>;
  matchDuration: number;
  /** Round window — autoSchedule restricts slot generation to this. */
  roundStartDate: string;
  roundEndDate: string;
  existingBookings: Array<{ court_id: string; start_time: string; end_time: string }>;
  existingPlacedMatches: Array<{ court_id: string | null; scheduled_date: string | null; scheduled_time: string | null; home_team_id: string; away_team_id: string }>;
};

export function buildAutoScheduleInput(args: AutoScheduleArgs): ScheduleInput {
  const {
    matchesToPlace,
    leaguePlaySchedule,
    divisions,
    courts,
    matchDuration,
    roundStartDate,
    roundEndDate,
    existingBookings,
    existingPlacedMatches,
  } = args;

  // Map bookings.start_time/end_time (timestamptz) → autoSchedule's
  // { date, startMin, endMin } shape (Stockholm-local). Same Phase 0
  // Fix 1 derivation pattern.
  const existingBookingsMapped: ScheduleInput["existingBookings"] = existingBookings.map((b) => {
    const start = new Date(b.start_time);
    const end = new Date(b.end_time);
    return {
      court_id: b.court_id,
      date: format(start, "yyyy-MM-dd"),
      startMin: start.getHours() * 60 + start.getMinutes(),
      endMin: end.getHours() * 60 + end.getMinutes(),
    };
  });

  // Re-key league_matches.scheduled_date / scheduled_time → autoSchedule's
  // { date, time } existingMatches shape. Phase 0 Fix 2 contract.
  const existingMatchesMapped: ScheduleInput["existingMatches"] = existingPlacedMatches
    .filter((m) => m.court_id && m.scheduled_date && m.scheduled_time)
    .map((m) => ({
      court_id: m.court_id as string,
      date: m.scheduled_date as string,
      time: (m.scheduled_time as string).slice(0, 5),
      home_team_id: m.home_team_id,
      away_team_id: m.away_team_id,
    }));

  // Divisions for autoSchedule — only those with non-null play_schedule.
  const divisionsForSchedule = divisions
    .filter((d) => d.playSchedule && Object.keys(d.playSchedule).length > 0)
    .map((d) => ({ id: d.id, play_schedule: d.playSchedule! }));

  return {
    matches: matchesToPlace.map((m) => ({
      id: m.id,
      round: m.round,
      home_team_id: m.home_team_id,
      away_team_id: m.away_team_id,
      division_id: m.division_id ?? undefined,
    })),
    playSchedule: leaguePlaySchedule ?? {},
    courts,
    matchDuration,
    startDate: roundStartDate,
    endDate: roundEndDate,
    existingBookings: existingBookingsMapped,
    existingMatches: existingMatchesMapped,
    divisions: divisionsForSchedule,
  };
}

// ─── I/O orchestrator ────────────────────────────────────────────────────

type RoundRow = Tables<"league_rounds">;
type LeagueRow = Tables<"leagues">;

/**
 * Generate per-omgång matches and place them on courts/timeslots in a
 * single transaction (logical — Supabase JS client doesn't expose real
 * transactions, so we use a best-effort sequence with rollback on
 * insert failure).
 *
 * Behavior:
 *   1. Fetch the round + league + divisions + teams (with first 2
 *      primaries by joined_at) + courts.
 *   2. If skipIfMatchesExist (default true): bail per-division if any
 *      league_matches with this round_id + division_id already exist.
 *   3. Build inserts via buildRoundMatchInserts() (pure).
 *   4. Bulk INSERT into league_matches.
 *   5. Fetch existing bookings + existing placed matches (other rounds
 *      in same league) for autoSchedule context.
 *   6. Build autoSchedule input via buildAutoScheduleInput() (pure).
 *   7. Run autoSchedule → placements.
 *   8a. Sprint 7 Part 2 — auto-create FREE bookings for placed matches:
 *       INSERT into bookings (court+start+end+duration, price=0,
 *       free_reason='league_match', user_id=created_by=auth.uid()).
 *       Throws if no authenticated user when placements > 0.
 *   8b. Bulk UPDATE league_matches with placements + booking_id linkage
 *       (canonical scheduled_date/scheduled_time/court_id per Phase 0 Fix 2).
 *   9. Return diagnostics.
 *
 * Errors:
 *   - Round not found → throws.
 *   - League not found → throws.
 *   - autoSchedule throws → caught, surfaced as result.unplacedReasons
 *     (matches still created, just unplaced).
 */
export async function generateMatchesForRound(
  supabase: SupabaseClient<Database>,
  args: { roundId: string; leagueId: string; skipIfMatchesExist?: boolean },
): Promise<GenerateMatchesForRoundResult> {
  const skipIfMatchesExist = args.skipIfMatchesExist ?? true;

  // 1a. Fetch the round row.
  const { data: round, error: rndErr } = await supabase
    .from("league_rounds")
    .select("id, league_id, round_number, name, start_date, end_date, status")
    .eq("id", args.roundId)
    .single();
  if (rndErr) throw new Error(`Round not found: ${rndErr.message}`);
  const roundRow = round as RoundRow;
  if (roundRow.league_id !== args.leagueId) {
    throw new Error("roundId does not belong to leagueId");
  }

  // 1b. Fetch the league row (for ball_holder_principle + play_schedule +
  // match_duration_minutes + organization for courts lookup).
  const { data: league, error: lgErr } = await supabase
    .from("leagues")
    .select("*")
    .eq("id", args.leagueId)
    .single();
  if (lgErr) throw new Error(`League not found: ${lgErr.message}`);
  const leagueRow = league as LeagueRow;

  // 1c. Fetch divisions with play_schedule. Path D: also pull `name` for
  // human-readable unplacedReasons in the UI.
  const { data: divisionsRaw, error: divErr } = await supabase
    .from("divisions")
    .select("id, name, play_schedule")
    .eq("league_id", args.leagueId);
  if (divErr) throw new Error(`Divisions fetch failed: ${divErr.message}`);

  // 1d. Fetch teams with members embed (post TASK-67 PR-D shape). Path D:
  // also pull `team_name` for the same UI surfacing.
  const { data: teamsRaw, error: teamErr } = await supabase
    .from("teams")
    .select("id, team_name, division_id, members:team_members(user_id, customer_id, role, joined_at)")
    .eq("league_id", args.leagueId);
  if (teamErr) throw new Error(`Teams fetch failed: ${teamErr.message}`);

  // 1e. Fetch courts for this league's organization (via halls join).
  const { data: courtsRaw, error: courtErr } = await supabase
    .from("courts")
    .select("id, hall_id, halls!inner(organization_id)")
    .eq("halls.organization_id", (leagueRow as unknown as { organization_id: string }).organization_id);
  if (courtErr) throw new Error(`Courts fetch failed: ${courtErr.message}`);

  // 2. Build per-division team list + check skip-if-exists.
  // PR-1b: `members.customer_id` now included so customer-only team
  // members feed the dual-FK ball-holder picker (see extractPrimariesForBallHolder).
  type TeamWithMembers = {
    id: string;
    team_name: string | null;
    division_id: string | null;
    members: Array<{ user_id: string | null; customer_id: string | null; role: "primary" | "reserve"; joined_at: string }> | null;
  };
  const teamsRows = ((teamsRaw ?? []) as unknown as TeamWithMembers[]) ?? [];

  let divisionsSkipped = 0;
  const divisions: DivisionForGeneration[] = [];

  if (skipIfMatchesExist) {
    const { data: existingMatchesByDiv } = await supabase
      .from("league_matches")
      .select("division_id, round_id")
      .eq("league_id", args.leagueId)
      .eq("round_id", args.roundId);
    const existingDivisionIds = new Set(
      (existingMatchesByDiv ?? [])
        .map((m: { division_id: string | null }) => m.division_id)
        .filter((d): d is string => !!d),
    );
    for (const div of divisionsRaw ?? []) {
      if (existingDivisionIds.has(div.id)) {
        divisionsSkipped++;
        continue;
      }
      const divTeams = teamsRows.filter((t) => t.division_id === div.id);
      // PR-1b: shared `extractPrimariesForBallHolder` helper handles both
      // user_id-backed and customer_id-backed primaries identically.
      const teamsForGen = divTeams.map((t) => ({
        id: t.id,
        ...extractPrimariesForBallHolder(t.members),
      }));
      divisions.push({
        id: div.id,
        teams: teamsForGen,
        playSchedule: (div.play_schedule as DivisionForGeneration["playSchedule"]) ?? null,
      });
    }
  } else {
    // skipIfMatchesExist=false → process every division regardless. Caller
    // is responsible for cleanup if collision occurs (we don't auto-DELETE
    // existing matches).
    for (const div of divisionsRaw ?? []) {
      const divTeams = teamsRows.filter((t) => t.division_id === div.id);
      // PR-1b: same shared helper as the skipIfMatchesExist=true branch.
      const teamsForGen = divTeams.map((t) => ({
        id: t.id,
        ...extractPrimariesForBallHolder(t.members),
      }));
      divisions.push({
        id: div.id,
        teams: teamsForGen,
        playSchedule: (div.play_schedule as DivisionForGeneration["playSchedule"]) ?? null,
      });
    }
  }

  // 3. Build inserts (pure).
  const principle: BallHolderPrinciple =
    ((leagueRow as unknown as { ball_holder_principle: BallHolderPrinciple | null }).ball_holder_principle)
    ?? "alternating_pair";
  const { inserts, divisionsProcessed } = buildRoundMatchInserts({
    roundId: args.roundId,
    leagueId: args.leagueId,
    divisions,
    ballHolderPrinciple: principle,
  });

  if (inserts.length === 0) {
    return {
      roundId: args.roundId,
      divisionsProcessed,
      matchesCreated: 0,
      matchesPlaced: 0,
      matchesUnplaced: 0,
      unplacedReasons: [],
      ...(divisionsSkipped > 0
        ? { skipped: { reason: "matches_exist_for_round" as const, divisionsSkipped } }
        : {}),
    };
  }

  // 4. Bulk INSERT.
  const { data: insertedRaw, error: insErr } = await supabase
    .from("league_matches")
    .insert(inserts)
    .select("id, round, home_team_id, away_team_id, division_id");
  if (insErr) throw new Error(`Match insert failed: ${insErr.message}`);
  const inserted = (insertedRaw ?? []) as Array<{
    id: string;
    round: number;
    home_team_id: string;
    away_team_id: string;
    division_id: string | null;
  }>;

  // 5. Fetch existing bookings in [round.start_date, round.end_date].
  const bookingsWindowEndExclusive = addDays(parseISO(roundRow.end_date), 1).toISOString();
  const { data: bookingsRaw } = await supabase
    .from("bookings")
    .select("court_id, start_time, end_time")
    .gte("start_time", roundRow.start_date)
    .lt("start_time", bookingsWindowEndExclusive);

  // 6. Fetch existing placed matches in this league (any round).
  const { data: placedRaw } = await supabase
    .from("league_matches")
    .select("court_id, scheduled_date, scheduled_time, home_team_id, away_team_id")
    .eq("league_id", args.leagueId)
    .not("court_id", "is", null);

  // 7. Build autoSchedule input + run.
  const matchDuration = (leagueRow as unknown as { match_duration_minutes: number | null }).match_duration_minutes ?? 90;
  const leaguePlaySchedule =
    ((leagueRow as unknown as { play_schedule: Record<string, { start: string; end: string }[]> | null })
      .play_schedule) ?? null;

  const courtsList: Array<{ id: string }> = (courtsRaw ?? []).map(
    (c: { id: string }) => ({ id: c.id }),
  );

  let placements: ReturnType<typeof autoSchedule> = [];
  let placementError: string | null = null;
  try {
    const scheduleInput = buildAutoScheduleInput({
      matchesToPlace: inserted,
      leaguePlaySchedule,
      divisions,
      courts: courtsList,
      matchDuration,
      roundStartDate: roundRow.start_date,
      roundEndDate: roundRow.end_date,
      existingBookings: (bookingsRaw ?? []).map((b: { court_id: string; start_time: string; end_time: string }) => b),
      existingPlacedMatches: (placedRaw ?? []) as Array<{
        court_id: string | null;
        scheduled_date: string | null;
        scheduled_time: string | null;
        home_team_id: string;
        away_team_id: string;
      }>,
    });
    placements = autoSchedule(scheduleInput);
  } catch (err: unknown) {
    placementError = err instanceof Error ? err.message : String(err);
  }

  // 8a. Sprint 7 Part 2 — auto-create FREE bookings for placed matches.
  //
  // For every placed league_match we insert a `bookings` row at the same
  // court+time and link via `league_matches.booking_id`. The booking is
  // attributed to the publishing admin (auth.uid()) and marked FREE
  // (price_total=0, free_reason='league_match'). MATCHi sync is currently
  // paused globally — when it re-activates, the existing
  // `sync_booking_to_league_match` AFTER-UPDATE trigger handles the linkage
  // identically to manually-created bookings. The Sprint 7 Part 1
  // cancellation-lock (24h, league-linked) already applies because the new
  // bookings are linked via `league_matches.booking_id`.
  //
  // Unplaced matches do not get a booking — they have no court/date/time
  // yet. When/if an admin manually places them later via the calendar UI,
  // that flow creates the booking + links it.
  type PlacementWithBooking = {
    matchId: string;
    courtId: string;
    date: string;
    time: string;
    bookingId: string;
  };
  const placementBookings: PlacementWithBooking[] = [];

  if (placements.length > 0) {
    // Resolve publishing admin uid for created_by + user_id attribution.
    const { data: authData, error: authErr } = await supabase.auth.getUser();
    const publisherUid = authData?.user?.id;
    if (authErr || !publisherUid) {
      throw new Error(
        `Cannot auto-create bookings without authenticated user: ${authErr?.message ?? "no session"}`,
      );
    }

    // Build per-placement booking inserts. Compose start/end as UTC ISO
    // strings from the Stockholm wall-clock (scheduled_date, scheduled_time).
    const bookingInserts = placements.map((p) => {
      const start = fromStockholmWallClock(p.date, p.time);
      const end = addMinutes(start, matchDuration);
      return {
        court_id: p.courtId,
        user_id: publisherUid,
        created_by: publisherUid,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        duration_minutes: matchDuration,
        price_per_hour: 0,
        price_total: 0,
        price_total_minor: 0,
        free_reason: "league_match",
        info_text: "Auto-genererad ligamatch",
      } satisfies Partial<Tables<"bookings">>;
    });

    const { data: createdBookings, error: bookErr } = await supabase
      .from("bookings")
      .insert(bookingInserts as TablesInsert<"bookings">[])
      .select("id");
    if (bookErr) throw new Error(`Auto-booking insert failed: ${bookErr.message}`);
    if (!createdBookings || createdBookings.length !== placements.length) {
      throw new Error(
        `Auto-booking insert returned ${createdBookings?.length ?? 0} rows, expected ${placements.length}`,
      );
    }

    // Pair placements with their newly-created bookings (order preserved).
    for (let i = 0; i < placements.length; i++) {
      const p = placements[i];
      placementBookings.push({
        matchId: p.matchId,
        courtId: p.courtId,
        date: p.date,
        time: p.time,
        bookingId: createdBookings[i].id,
      });
    }
  }

  // 8b. Bulk UPDATE placements (canonical column names per Phase 0 Fix 2)
  // including the new booking_id linkage. Unplaced matches are not touched.
  for (const pb of placementBookings) {
    await supabase
      .from("league_matches")
      .update({
        court_id: pb.courtId,
        scheduled_date: pb.date,
        scheduled_time: pb.time,
        booking_id: pb.bookingId,
      })
      .eq("id", pb.matchId);
  }

  // 9. Diagnostics. Path D: enrich with home/away/division names so the
  // toast can show "<home> vs <away> (<division>): <reason>" without a
  // second round-trip. Names come from the same teamsRows + divisionsRaw
  // we already fetched in steps 1c/1d.
  const placedIds = new Set(placements.map((p) => p.matchId));
  const teamNameById = new Map<string, string | null>(
    teamsRows.map((t) => [t.id, t.team_name]),
  );
  type DivisionRow = { id: string; name: string | null };
  const divisionNameById = new Map<string, string | null>(
    ((divisionsRaw ?? []) as DivisionRow[]).map((d) => [d.id, d.name]),
  );
  const unplacedReasons: GenerateMatchesForRoundResult["unplacedReasons"] = inserted
    .filter((m) => !placedIds.has(m.id))
    .map((m) => ({
      matchId: m.id,
      reason: placementError ?? "no_available_slot",
      homeTeamName: teamNameById.get(m.home_team_id) ?? null,
      awayTeamName: teamNameById.get(m.away_team_id) ?? null,
      divisionName: m.division_id ? divisionNameById.get(m.division_id) ?? null : null,
    }));

  return {
    roundId: args.roundId,
    divisionsProcessed,
    matchesCreated: inserted.length,
    matchesPlaced: placements.length,
    matchesUnplaced: unplacedReasons.length,
    unplacedReasons,
    ...(divisionsSkipped > 0
      ? { skipped: { reason: "matches_exist_for_round" as const, divisionsSkipped } }
      : {}),
  };
}
